import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type {
  Vehicle,
  VehicleIndividualParameterDownload,
  VehicleIndividualParameterUpload,
  VehicleIndividualParameterVersion,
} from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import axios from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type DescriptionBody = {
  path: '/description';
  value: string;
};

type Body = DescriptionBody;

export const useVehicleIndividualParameterAPI = () => {
  const [uploadUrlState, setUploadUrlState] = useState<APIState>('none');
  const [downloadUrlState, setDownloadUrlState] = useState<APIState>('none');
  const [deployState, setDeployState] = useState<APIState>('none');
  const [parameterVersionsState, setParameterVersionsState] =
    useState<APIState>('none');
  const [fileUploadState, setFileUploadState] = useState<APIState>('none');
  const [fileDownloadState, setFileDownloadState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postParameterUploadUrlRequest = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
    ): Promise<Result<VehicleIndividualParameterUpload, AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/calibration_parameters/upload_links`,
        );
        return new Success(res.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postParameterUploadUrl = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_vehicle_parameter_upload_url', {
              status: 'failed',
            }),
          });
          setUploadUrlState('hasError');
          return null;
        }
        setUploadUrlState('loading');
        const res = await postParameterUploadUrlRequest(
          project!.id,
          environment!.environment_id,
          vehicleId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_vehicle_parameter_upload_url', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setUploadUrlState('hasError');
          return null;
        }
        setUploadUrlState('hasValue');
        return res.value;
      },
      [notifyError, postParameterUploadUrlRequest, t, getErrorMessage],
    ),
  );

  const postParameterDownloadUrlRequest = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      versionId: string,
    ): Promise<Result<VehicleIndividualParameterDownload, AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/calibration_parameters/${versionId}/download_links`,
        );
        return new Success(res.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postParameterDownloadUrl = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, versionId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_vehicle_parameter_download_url', {
              status: 'failed',
            }),
          });
          setDownloadUrlState('hasError');
          return null;
        }
        setDownloadUrlState('loading');
        const res = await postParameterDownloadUrlRequest(
          project!.id,
          environment!.environment_id,
          vehicleId,
          versionId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_vehicle_parameter_download_url', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setDownloadUrlState('hasError');
          return null;
        }
        setDownloadUrlState('hasValue');
        return res.value;
      },
      [notifyError, postParameterDownloadUrlRequest, t, getErrorMessage],
    ),
  );

  const postParameterDeployRequest = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      versionId: string,
    ): Promise<Result<Vehicle, AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/calibration_parameters/${versionId}/deploy`,
        );
        return new Success(res.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postParameterDeploy = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, versionId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_vehicle_parameter_deploy', {
              status: 'failed',
            }),
          });
          setDeployState('hasError');
          return null;
        }
        setDeployState('loading');
        const res = await postParameterDeployRequest(
          project!.id,
          environment!.environment_id,
          vehicleId,
          versionId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_vehicle_parameter_deploy', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setDeployState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.post_vehicle_parameter_deploy', {
            status: 'successfully',
          }),
        });
        setDeployState('hasValue');
        return res.value;
      },
      [
        notifyError,
        notifySuccess,
        postParameterDeployRequest,
        t,
        getErrorMessage,
      ],
    ),
  );

  const getParameterVersionsRequest = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
    ): Promise<Result<VehicleIndividualParameterVersion[], AxiosResponse>> => {
      try {
        const res = await fmsAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/calibration_parameters`,
        );
        return new Success(res.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getParameterVersions = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.get_vehicle_parameters', { status: 'failed' }),
          });
          setParameterVersionsState('hasError');
          return [];
        }
        setParameterVersionsState('loading');
        const res = await getParameterVersionsRequest(
          project!.id,
          environment!.environment_id,
          vehicleId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.get_vehicle_parameters', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setParameterVersionsState('hasError');
          return [];
        }
        setParameterVersionsState('hasValue');
        return res.value;
      },
      [notifyError, getParameterVersionsRequest, t, getErrorMessage],
    ),
  );

  const putParameterFileUpload = useCallback(
    async (uploadUrl: string, file: Blob): Promise<boolean> => {
      setFileUploadState('loading');
      try {
        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
        });
        notifySuccess({
          message: t('api.fms.put_vehicle_parameter_file_upload', {
            status: 'successfully',
          }),
        });
        setFileUploadState('hasValue');
        return true;
      } catch {
        notifyError({
          message: t('api.fms.put_vehicle_parameter_file_upload', {
            status: 'failed',
          }),
        });
        setFileUploadState('hasError');
        return false;
      }
    },
    [notifyError, notifySuccess, t],
  );

  const getParameterFileDownload = useCallback(
    async (downloadUrl: string): Promise<Blob | null> => {
      setFileDownloadState('loading');
      try {
        const res = await axios.get(downloadUrl, { responseType: 'blob' });
        setFileDownloadState('hasValue');
        return res.data;
      } catch (error) {
        console.log(error);
        notifyError({
          message: t('api.fms.get_vehicle_parameter_file', {
            status: 'failed',
          }),
        });
        setFileDownloadState('hasError');
        return null;
      }
    },
    [notifyError, t],
  );

  const patchParameterVersionsRequest = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      versionId: string,
      body: Body,
    ): Promise<Result<VehicleIndividualParameterVersion, AxiosResponse>> => {
      try {
        const res = await fmsAPI.patch(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/calibration_parameters/${versionId}`,
          [
            {
              ...body,
              op: 'replace',
            },
          ],
        );
        notifySuccess({
          message: t('api.fms.patch_vehicle_parameters.description', {
            status: 'successfully',
          }),
        });
        return new Success(res.data);
      } catch (error) {
        notifyError({
          message: t('api.fms.patch_vehicle_parameters.description', {
            status: 'failed',
          }),
        });
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI, notifySuccess, notifyError, t],
  );

  const patchParameterVersionDescription = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        vehicleId: string,
        versionId: string,
        description: string,
      ) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.patch_vehicle_parameters.description', {
              status: 'failed',
            }),
          });
          setParameterVersionsState('hasError');
          return null;
        }
        setParameterVersionsState('loading');
        const res = await patchParameterVersionsRequest(
          project!.id,
          environment!.environment_id,
          vehicleId,
          versionId,
          {
            path: '/description',
            value: description,
          },
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.patch_vehicle_parameters.description', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setParameterVersionsState('hasError');
          return null;
        }

        setParameterVersionsState('hasValue');
        return res.value;
      },
      [notifyError, patchParameterVersionsRequest, t, getErrorMessage],
    ),
  );
  return {
    uploadUrlState,
    postParameterUploadUrl,
    fileUploadState,
    putParameterFileUpload,
    downloadUrlState,
    postParameterDownloadUrl,
    deployState,
    postParameterDeploy,
    parameterVersionsState,
    getParameterVersions,
    fileDownloadState,
    getParameterFileDownload,
    patchParameterVersionDescription,
  };
};
